import {
  Menu,
  Icon,
  MenuItem,
  MenuList,
  Portal,
  MenuButton,
  Image,
  Link,
  Text,
  Tooltip,
  useCallbackRef,
} from '@chakra-ui/react';
import { isBefore } from 'date-fns';
import { useRouter } from 'next/router';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import CalendarIcon from '@/assets/icons/ic-calendar-plus.svg';

import { useAddEventToCalendarLinks } from '@/components/ui/shared/buttons/AddToCalendar/AddToCalendar.hooks';
import {
  iconStyleProps,
  menuButtonStyleProps,
  simpleMButtonStyleProps,
  menuItemStyleProps,
} from '@/components/ui/shared/buttons/AddToCalendar/AddToCalendar.styles';
import { IAddToCalendar } from '@/components/ui/shared/buttons/AddToCalendar/type';

export const AddToCalendar: FC<IAddToCalendar> = ({
  color,
  allDayEvent = false,
  size = 22,
  iconSize = 20,
  tooltipPlacement = 'start',
  simpleMenuButton = false,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  ...rest
}) => {
  const { asPath } = useRouter();
  const handleMousedown = useCallbackRef((e) => {
    e.stopPropagation();
  });

  const handleMenuListClick = useCallbackRef((e) => {
    e.stopPropagation();
  });

  const handleMenuButtonClick = useCallbackRef((e) => {
    e.stopPropagation();
  });

  const handleMenuOpen = () => {
    document.addEventListener('mousedown', handleMousedown, { capture: true });
  };
  const handleMenuClose = () => {
    document.removeEventListener('mousedown', handleMousedown);
  };

  const url = process.env.NEXT_PUBLIC_BASE_URL + asPath.substring(1);
  const calendarLinks = useAddEventToCalendarLinks({ allDayEvent, timeZone, url, ...rest });
  const { t } = useTranslation();

  const menuProps = simpleMenuButton ? simpleMButtonStyleProps : menuButtonStyleProps;

  const isFinished = isBefore(new Date(rest.endUtc), new Date());

  if (isFinished) return null;

  return (
    <Menu onOpen={handleMenuOpen} onClose={handleMenuClose}>
      <Tooltip label={t('addToCalendar.tooltip')} placement={tooltipPlacement} hasArrow bg={'white'}>
        <MenuButton
          aria-label="Add to calendar"
          w={`${size}px`}
          h={`${size}px`}
          onClick={handleMenuButtonClick}
          {...menuProps}
        >
          <Icon
            as={CalendarIcon}
            w={`${iconSize}px`}
            h={`${iconSize}px`}
            color={color}
            _groupHover={{ color: 'gray.50' }}
            {...iconStyleProps}
          />
        </MenuButton>
      </Tooltip>
      <Portal>
        <MenuList onClick={handleMenuListClick} color={'gray.500'} zIndex={100}>
          <MenuItem as={Link} isExternal href={calendarLinks.ics} download={'Apple Calendar'} {...menuItemStyleProps}>
            <Image boxSize={5} mr={2.5} src={'/images/apple-calendar.svg'} />
            <Text fontSize="md" fontWeight={100}>
              {t('addToCalendar.apple')}
            </Text>
          </MenuItem>
          <MenuItem as={Link} isExternal href={calendarLinks.google} {...menuItemStyleProps}>
            <Image boxSize={5} mr={2.5} src={'/images/google.svg'} />
            <Text fontSize="md" fontWeight={100}>
              {`${t('addToCalendar.goggle')} (${t('common.online')})`}
            </Text>
          </MenuItem>
          <MenuItem as={Link} isExternal href={calendarLinks.office365} {...menuItemStyleProps}>
            <Image boxSize={5} mr={2.5} src={'/images/office365.svg'} />
            <Text fontSize="md" fontWeight={100}>
              {`${t('addToCalendar.office365')} (${t('common.online')})`}
            </Text>
          </MenuItem>
          <MenuItem as={Link} isExternal href={calendarLinks.ics} download={'Outlook Calendar'} {...menuItemStyleProps}>
            <Image boxSize={5} mr={2.5} src={'/images/outlook.svg'} />
            <Text fontSize="md" fontWeight={100}>
              {t('addToCalendar.outlook')}
            </Text>
          </MenuItem>
          <MenuItem as={Link} isExternal href={calendarLinks.outlook} {...menuItemStyleProps}>
            <Image boxSize={5} mr={2.5} src={'/images/outlookcom.svg'} />
            <Text fontSize="md" fontWeight={100}>
              {`${t('addToCalendar.outlookcom')} (${t('common.online')})`}
            </Text>
          </MenuItem>
          <MenuItem as={Link} isExternal href={calendarLinks.yahoo} {...menuItemStyleProps}>
            <Image boxSize={5} mr={2.5} src={'/images/yahoo.svg'} />
            <Text fontSize="md" fontWeight={100}>
              {`${t('addToCalendar.yahoo')} (${t('common.online')})`}
            </Text>
          </MenuItem>
        </MenuList>
      </Portal>
    </Menu>
  );
};
